import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { UserAvatar } from "@/components/UserAvatar";
import { useToast } from "@/hooks/use-toast";
import { useAddTeamMember, useRemoveTeamMember, useTeamMembers } from "@/hooks/useTeams";
import { useUsers } from "@/hooks/useUsers";
import { cn } from "@/lib/utils";
import { Team, User } from "@/types";
import { Check, ChevronsUpDown, Loader2, UserPlus, X } from "lucide-react";
import { useEffect, useState } from "react";

interface TeamMemberAssignmentProps {
    open: boolean;
    onClose: () => void;
    team: Team;
}

export function TeamMemberAssignment({ open, onClose, team }: TeamMemberAssignmentProps) {
    const [selectedUserId, setSelectedUserId] = useState<string>("");
    const [selectedUserName, setSelectedUserName] = useState<string>("");
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [popoverOpen, setPopoverOpen] = useState(false);
    const { toast } = useToast();

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const { data: membersResponse, isLoading: loadingMembers } = useTeamMembers(team.pid);
    const { data: searchResponse, isLoading: loadingSearch } = useUsers(debouncedSearch);

    const members = membersResponse?.data || [];
    const searchedUsers = searchResponse?.data || [];
    const addMember = useAddTeamMember();
    const removeMember = useRemoveTeamMember();

    const memberPids = new Set(members.map(m => m.pid));
    const usersNotInTeam = searchedUsers.filter(user => !memberPids.has(user.pid));

    const handleAddMember = () => {
        if (!selectedUserId) return;

        addMember.mutate(
            { teamPid: team.pid, userPid: selectedUserId },
            {
                onSuccess: () => {
                    toast({
                        title: "Member added",
                        description: "Team member has been successfully added.",
                    });
                    setSelectedUserId("");
                    setSelectedUserName("");
                    setSearchQuery("");
                },
                onError: () => {
                    toast({
                        title: "Error",
                        description: "Failed to add team member. Please try again.",
                        variant: "destructive",
                    });
                },
            }
        );
    };

    const handleRemoveMember = (memberPid: string) => {
        removeMember.mutate(
            { teamPid: team.pid, userPid: memberPid },
            {
                onSuccess: () => {
                    toast({
                        title: "Member removed",
                        description: "Team member has been successfully removed.",
                    });
                },
                onError: () => {
                    toast({
                        title: "Error",
                        description: "Failed to remove team member. Please try again.",
                        variant: "destructive",
                    });
                },
            }
        );
    };

    const getMemberUser = (userId: string): User | undefined => {
        return searchedUsers.find(u => u.pid === userId) || members.find(m => m.pid === userId) as unknown as User;
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>Manage Team Members - {team.name}</DialogTitle>
                    <DialogDescription>
                        Add or remove members from this team.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Add Member Section */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Add Member</label>
                        <div className="flex gap-2">
                            <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={popoverOpen}
                                        className="flex-1 justify-between font-normal"
                                    >
                                        {selectedUserId
                                            ? selectedUserName
                                            : "Select a user to add..."}
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[450px] p-0" align="start">
                                    <Command shouldFilter={false}>
                                        <CommandInput
                                            placeholder="Type to search users..."
                                            value={searchQuery}
                                            onValueChange={setSearchQuery}
                                        />
                                        <CommandList>
                                            {loadingSearch ? (
                                                <div className="p-4 text-center text-sm text-muted-foreground">
                                                    <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                                                    Searching...
                                                </div>
                                            ) : usersNotInTeam.length === 0 ? (
                                                <CommandEmpty>
                                                    {debouncedSearch ? "No users found matching your search." : "Type to search for users to add."}
                                                </CommandEmpty>
                                            ) : (
                                                <CommandGroup>
                                                    {usersNotInTeam.map((user) => (
                                                        <CommandItem
                                                            key={user.pid}
                                                            value={user.pid}
                                                            onSelect={() => {
                                                                setSelectedUserId(user.pid);
                                                                setSelectedUserName(user.fullName);
                                                                setPopoverOpen(false);
                                                            }}
                                                            className="flex items-center justify-between"
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <UserAvatar
                                                                    src={user.avatarUrl}
                                                                    name={user.fullName}
                                                                    className="h-6 w-6"
                                                                    fallbackClassName="text-[10px]"
                                                                />
                                                                <div className="flex flex-col">
                                                                    <span className="text-sm">{user.fullName}</span>
                                                                    <span className="text-muted-foreground text-[10px]">{user.email}</span>
                                                                </div>
                                                            </div>
                                                            <Check
                                                                className={cn(
                                                                    "h-4 w-4",
                                                                    selectedUserId === user.pid ? "opacity-100" : "opacity-0"
                                                                )}
                                                            />
                                                        </CommandItem>
                                                    ))}
                                                </CommandGroup>
                                            )}
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                            <Button
                                onClick={handleAddMember}
                                disabled={!selectedUserId || addMember.isPending}
                                className="gap-2"
                            >
                                {addMember.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <UserPlus className="h-4 w-4" />
                                )}
                                Add
                            </Button>
                        </div>
                    </div>

                    {/* Current Members List */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium">
                            Current Members ({membersResponse?.total})
                        </label>
                        <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
                            {loadingMembers ? (
                                <div className="p-4 text-center text-sm text-muted-foreground">
                                    <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                                    Loading members...
                                </div>
                            ) : members.length > 0 ? (
                                members.map((member) => (
                                    <div key={member.pid} className="flex items-center justify-between p-3 hover:bg-muted/50">
                                        <div className="flex items-center gap-3">
                                            <UserAvatar
                                                src={member.avatarUrl}
                                                name={member.fullName}
                                                className="h-9 w-9"
                                                fallbackClassName="text-sm"
                                            />
                                            <div>
                                                <p className="text-sm font-medium">{member.fullName}</p>
                                                <p className="text-xs text-muted-foreground">{member.email}</p>
                                            </div>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleRemoveMember(member.pid)}
                                            disabled={removeMember.isPending}
                                            className="h-8 w-8"
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ))
                            ) : (
                                <div className="p-4 text-center text-sm text-muted-foreground">
                                    No members in this team yet
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
